

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ImageFileUpload, postRequest } from '../Utility/Utility';
import './VoxPlayer.css';

const STATEMENTS = [
  "The quick brown fox jumps over the lazy dog.",
  "A journey of a thousand miles begins with a single step.",
  "Every cloud has a silver lining.",
  "Time flies when you are having fun.",
];

const STATE = { 
  IDLE: 'idle', 
  RECORDING: 'recording', 
  REVIEW: 'review', 
  VERIFYING: 'verifying', 
  FAILED: 'failed', 
  SUCCESS: 'success' 
};

const formatTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const mins = Math.floor(s / 60);
  const secs = Math.floor(s % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

function useRecorder() {
  const mr = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const [rec, setRec] = useState(false);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);

  const start = useCallback(async () => {
    try {
      setErr(null);
      chunks.current = [];
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      const m = new MediaRecorder(s);
      mr.current = m;
      
      m.ondataavailable = e => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };
      
      m.onstop = () => {
        const b = new Blob(chunks.current, { type: 'audio/webm' });
        setBlob(b);
        setUrl(URL.createObjectURL(b));
        s.getTracks().forEach(t => t.stop());
      };
      
      m.start(100);
      setRec(true);
    } catch (e) {
      setErr(e.name === 'NotAllowedError' ? 'Microphone access denied' : e.message);
    }
  }, []);

  const stop = useCallback(() => {
    if (mr.current && rec) {
      mr.current.stop();
      setRec(false);
    }
  }, [rec]);

  const reset = useCallback(() => {
    if (url) URL.revokeObjectURL(url);
    setBlob(null);
    setUrl(null);
    setErr(null);
    chunks.current = [];
  }, [url]);

  return { rec, blob, url, err, start, stop, reset };
}

export default function VoxPlayer() {
  const [state, setState] = useState(STATE.IDLE);
  const [stmt, setStmt] = useState(STATEMENTS[Math.floor(Math.random() * STATEMENTS.length)]);
  const [status, setStatus] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const { rec, blob, url, err, start, stop, reset } = useRecorder();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const animFrameRef = useRef(null);


  const params = new URLSearchParams(window.location.search);

  const voicePlayerId = params.get("uid")?.replace(/"/g, "") || "";

  console.log("VOICE PLAYER ID FROM URL:", voicePlayerId);
  console.log("FULL URL:", window.location.href);
  console.log("QUERY STRING:", window.location.search);






  useEffect(() => {
    if (rec) {
      setState(STATE.RECORDING);
      setStatus(null);
      setElapsed(0);
    } else if (blob && state === STATE.RECORDING) {
      setState(STATE.REVIEW);
    }
  }, [rec, blob, state]);

  useEffect(() => {
    if (err) {
      setStatus({ t: 'error', m: err });
      setState(STATE.IDLE);
    }
  }, [err]);

  useEffect(() => {
    if (state !== STATE.RECORDING) return;
    const i = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(i);
  }, [state]);

  // Get duration using AudioContext when blob is ready
  useEffect(() => {
    if (!blob) return;
    
    const getDuration = async () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const duration = audioBuffer.duration;
        console.log('Duration from AudioContext:', duration);
        setDur(duration);
        audioContext.close();
      } catch (error) {
        console.error('Error getting duration:', error);
      }
    };
    
    getDuration();
  }, [blob]);

  // Update time using requestAnimationFrame for smooth updates
  useEffect(() => {
    const updateTime = () => {
      if (audioRef.current && !audioRef.current.paused && !audioRef.current.ended) {
        setTime(audioRef.current.currentTime);
        animFrameRef.current = requestAnimationFrame(updateTime);
      }
    };

    if (playing && audioRef.current) {
      updateTime();
    }

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [playing]);


  useEffect(() => {
    const a = audioRef.current;
    if (!a || !url) return;

    const onPlay = () => {
      console.log('Audio started playing');
      setPlaying(true);
    };
    
    const onPause = () => {
      console.log('Audio paused');
      setPlaying(false);
    };
    
    const onEnded = () => {
      console.log('Audio ended');
      setPlaying(false);
      setTime(0);
      if (a) a.currentTime = 0;
    };

    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);

    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
    };
  }, [url]);

  const onRecord = () => rec ? stop() : start();
  
  const onReset = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
    setTime(0);
    setDur(0);
    reset();
    setState(STATE.IDLE);
    setStatus(null);
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a || !url) {
      console.log('No audio element or URL');
      return;
    }

    console.log('Toggle clicked. Paused:', a.paused, 'Ready state:', a.readyState);

    if (a.paused) {
      console.log('Attempting to play...');
      const playPromise = a.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
          
            setPlaying(true);
          })
          .catch(e => {
  
            setPlaying(false);
            setStatus({ t: 'error', m: 'Audio playback failed. Try again.' });
          });
      }
    } else {
      console.log('Pausing audio');
      a.pause();
      setPlaying(false);
    }
  };

  const handleProgressClick = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * dur;

    a.currentTime = newTime;
    setTime(newTime);
  };

  const onSubmit = async () => {
  if (!blob) return;

  // Safety: check player id
  if (!voicePlayerId) {
    setStatus({ t: 'error', m: 'Invalid Player ID' });
    return;
  }

  setState(STATE.VERIFYING);
  setStatus({ t: 'loading', m: 'Uploading audio...' });

  try {
    const file = new File([blob], 'recording.webm', { type: 'audio/webm' });

    // Upload audio file first
    const uploadRes = await ImageFileUpload(
      'biostarapp/user/file-upload',
      file,
      'file'
    );

    let fileUrl = '';

    if (Array.isArray(uploadRes?.data) && uploadRes.data.length > 0) {
      fileUrl = uploadRes.data[0];
    } else if (typeof uploadRes?.data === 'string') {
      fileUrl = uploadRes.data;
    } else {
      fileUrl = uploadRes?.fileUrl || uploadRes?.url || uploadRes?.path;
    }

    if (!fileUrl) {
      throw new Error('File upload failed');
    }

    setStatus({ t: 'loading', m: 'Submitting recording...' });

    // ✅ Send BOTH file + voicePlayerId
    const submitRes = await postRequest(
      'biostarapp/user/client/upload/voxplayer/file',
      {
        file: fileUrl,
        voicePlayerId: voicePlayerId
      }
    );

    setState(STATE.SUCCESS);
    setStatus({ t: 'success', m: submitRes?.msg || 'Recording submitted!' });

    setTimeout(() => {
      onReset();
      setStmt(STATEMENTS[Math.floor(Math.random() * STATEMENTS.length)]);
    }, 3000);

  } catch (err) {
    setState(STATE.FAILED);
    setStatus({
      t: 'error',
      m: err?.response?.data?.msg || err?.message || 'Submission failed. Try again.'
    });
  }
};



  // const onSubmit = async () => {
  //   if (!blob) return;
    
  //   setState(STATE.VERIFYING);
  //   setStatus({ t: 'loading', m: 'Uploading audio...' });
    
  //   try {
  //     const file = new File([blob], 'recording.webm', { type: 'audio/webm' });
      
  //     const uploadRes = await ImageFileUpload('biostarapp/user/file-upload', file, 'file');
  //     setStatus({ t: 'loading', m: 'Submitting recording...' });
      
  //     let fileUrl = '';
  //     if (Array.isArray(uploadRes?.data) && uploadRes.data.length > 0) {
  //       fileUrl = uploadRes.data[0];
  //     } else if (typeof uploadRes?.data === 'string') {
  //       fileUrl = uploadRes.data;
  //     } else {
  //       fileUrl = uploadRes?.fileUrl || uploadRes?.url || uploadRes?.path;
  //     }
      
  //     const submitRes = await postRequest('biostarapp/user/client/upload/voxplayer/file', { 
  //       file: fileUrl 
  //     });
      
  //     setState(STATE.SUCCESS);
  //     setStatus({ t: 'success', m: submitRes?.msg || 'Recording submitted!' });
      
  //     setTimeout(() => {
  //       onReset();
  //       setStmt(STATEMENTS[Math.floor(Math.random() * STATEMENTS.length)]);
  //     }, 3000);
  //   } catch (err) {
  //     setState(STATE.FAILED);
  //     setStatus({ 
  //       t: 'error', 
  //       m: err?.response?.data?.msg || err?.message || 'Submission failed. Try again.' 
  //     });
  //   }
  // };

  const canRec = state === STATE.IDLE || state === STATE.FAILED;
  const canSub = state === STATE.REVIEW;
  const proc = state === STATE.VERIFYING || state === STATE.SUCCESS;

  return (
    <div className="vox-container">
      <div className="vox-card">
        <div className="vox-content">
          
          {/* Header */}
          <header className="vox-header">
            <div className="vox-logo">
              <div className="vox-icon">
                <span className="vox-ring"></span>
                <span className="vox-ring"></span>
                <span className="vox-ring"></span>
                <svg className="vox-mic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5">
                  <path d="M12 2a3 3 0 00-3 3v7a3 3 0 006 0V5a3 3 0 00-3-3z"/>
                  <path d="M19 10v2a7 7 0 01-14 0v-2"/>
                  <path d="M12 19v3"/>
                </svg>
              </div>
              <h1 className="vox-title">Witness Scan</h1>
              <span className="vox-badge">Pro</span>
            </div>
            <p className="vox-subtitle">Next-Gen Voice Capture</p>
          </header>

          {/* Statement Card */}
          <div className="statement-section">
            <div className="statement-label">
              <span className="statement-dot"></span>
              <span className="statement-text-label">Read aloud</span>
            </div>
            <div className={`statement-card ${state === STATE.RECORDING ? 'recording' : ''}`}>
              <p>{stmt}</p>
            </div>
          </div>

          {/* Recording Indicator */}
          {state === STATE.RECORDING && (
            <div className="recording-indicator">
              <div className="recording-badge">
                <span className="recording-dot"></span>
                <span className="recording-label">Recording</span>
                <span className="recording-time">{formatTime(elapsed)}</span>
                <div className="recording-wave">
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                </div>
              </div>
            </div>
          )}

          {/* Playback */}
          {url && state !== STATE.RECORDING && state !== STATE.SUCCESS && (
            <div className="playback-section">
              <audio ref={audioRef} src={url} preload="metadata" />
              <div className="playback-controls">
                <button onClick={toggle} className="play-button">
                  {playing ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                      <rect x="6" y="4" width="4" height="16" rx="1"/>
                      <rect x="14" y="4" width="4" height="16" rx="1"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </button>
                <div className="playback-progress">
                  <div className="progress-bar" onClick={handleProgressClick}>
                    <div 
                      className="progress-fill" 
                      style={{ width: `${dur > 0 ? (time/dur)*100 : 0}%` }}
                    />
                  </div>
                  <div className="progress-time">
                    <span className="progress-played">{formatTime(time)}</span>
                    <span className="progress-total">{formatTime(dur)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status */}
          {status && (
            <div className={`status-message ${status.t}`}>
              {status.t === 'loading' && <div className="status-spinner" />}
              {status.t === 'success' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                  <path d="M22 4L12 14.01l-3-3"/>
                </svg>
              )}
              {status.t === 'error' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M15 9l-6 6M9 9l6 6"/>
                </svg>
              )}
              <span className="status-text">{status.m}</span>
            </div>
          )}

          {/* Buttons */}
          <div className="button-group">
            {(canRec || state === STATE.RECORDING) && (
              <button 
                onClick={onRecord} 
                disabled={proc} 
                className={`vox-button ${state === STATE.RECORDING ? 'btn-stop' : 'btn-record'}`}
              >
                {state === STATE.RECORDING ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                      <rect x="6" y="6" width="12" height="12" rx="2"/>
                    </svg>
                    Stop Recording
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                      <circle cx="12" cy="12" r="5"/>
                    </svg>
                    {state === STATE.FAILED ? 'Try Again' : 'Begin Recording'}
                  </>
                )}
              </button>
            )}
            
            {canSub && (
              <button 
                onClick={onSubmit} 
                disabled={proc} 
                className="submit-button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
                Submit Recording
              </button>
            )}
            
            {state === STATE.REVIEW && (
              <button 
                onClick={onReset} 
                disabled={proc} 
                className="vox-button btn-reset"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8M3 3v5h5"/>
                </svg>
                Start Over
              </button>
            )}
          </div>

          {/* Hint */}
          {state === STATE.IDLE && (
            <div className="hint-box">
              <svg className="hint-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
              <p className="hint-text">
                Read the statement clearly. Your voice will be verified before submission.
              </p>
            </div>
          )}

        </div>

        {/* Footer */}
        <footer className="vox-footer">
          <div className="footer-status-update">
            <span className="status-dot"></span>
            Ready to record
          </div>
          <span className="footer-brand-update">Powered by Biostar</span>
        </footer>
      </div>
    </div>
  );
}